import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import TextInput from '../../components/customInput';
import SegmentedTab from '../../components/customTab';
import Button from '../../components/customButton';
import {
  loginUser,
  clearAuthError,
  selectAuthLoading,
  selectAuthError,
  selectUser
} from '../../redux/slices/authSlice';
import { landingRouteFor } from '../../routes/routeConfig';
import { notify } from '../../utils/notify';
import './index.scss';

const ApplicantIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M4 20c1.2-3.6 4.4-6 8-6s6.8 2.4 8 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const UnderwriterIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 21h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path
      d="M4 21V10.5M8.5 21V10.5M15.5 21V10.5M20 21V10.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M2.5 10.5 12 4l9.5 6.5H2.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

// Tab value -> the loginType your backend expects.
// This mapping is fixed in code. The user cannot change it.
const TAB_TO_LOGIN_TYPE = {
  applicant: 'APPLICANT',
  underwriter: 'INTERNAL'
};

const LoginPage = () => {
  const [tab, setTab] = useState('applicant');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const user = useSelector(selectUser);

  // When login succeeds, `user` fills in and we redirect based on their type.
  useEffect(() => {
    if (user) {
      navigate(landingRouteFor(user.userType), {
        replace: true
      });
    }
  }, [user, navigate]);

  // Clear a stale error when switching tabs.
  const handleTabChange = (value) => {
    setTab(value);
    dispatch(clearAuthError());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    const result = await dispatch(
      loginUser({
        login: email,
        password,
        loginType: TAB_TO_LOGIN_TYPE[tab]
      })
    );

    if (loginUser.fulfilled.match(result)) {
      notify.success(`Welcome back, ${result.payload.username}`);
    } else {
      notify.error(result.payload);
    }
  };

  return (
    <div className="login-page">
      <div className="login-panel-left">
        <div className="dot-grid" aria-hidden="true" />

        <div className="brand">
          <h1>CreditFlow</h1>
          <span>Loan Underwriting System</span>
        </div>

        <div className="pitch">
          <h2>
            Smart credit decisions,
            <em>powered by AI</em>
          </h2>

          <div className="stats-row">
            <div className="stat">
              <strong>6</strong>
              <span>Stage Pipeline</span>
            </div>
            <div className="stat">
              <strong>98%</strong>
              <span>Accuracy Rate</span>
            </div>
            <div className="stat">
              <strong>RBI</strong>
              <span>Compliant</span>
            </div>
          </div>
        </div>
      </div>

      <div className="login-panel-right">
        <div className="login-form-card">
          <h2 className="welcome-heading">Welcome back</h2>
          <p className="welcome-subtext">Sign in to continue to your portal</p>

          <SegmentedTab
            options={[
              { value: 'applicant', label: 'Applicant', icon: <ApplicantIcon /> },
              { value: 'underwriter', label: 'Underwriter', icon: <UnderwriterIcon /> }
            ]}
            iconClassName="tab-icon"
            active={tab}
            onChange={handleTabChange}
          />

          <form onSubmit={handleSubmit}>
            <TextInput
              label="Email Address"
              type="email"
              value={email}
              placeholder="you@email.com"
              onChange={(e) => setEmail(e.target.value)}
            />

            <TextInput
              label="Password"
              type="password"
              value={password}
              placeholder="Enter your password"
              onChange={(e) => setPassword(e.target.value)}
            />

            {error && <p className="login-error">{error}</p>}

            <Button type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in →'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
